import { Link } from 'react-router-dom';
import AnimatedOwlIcon from '../AnimatedOwlIcon';

/**
 * Footer — one simple row (logo + flat nav links), a divider, then a
 * bottom row (copyright + "not a law firm" disclaimer, reusing the wording
 * already live in src/Pages/legal/Terms.tsx). No socials row — there's no
 * real, official Defellix social link anywhere in the codebase to point it
 * at, so it's left out rather than pointing at a guessed URL.
 */

// Real routes with descriptive anchors (not #hash links): these are the pages
// search engines can offer as sitelinks under the homepage result.
const NAV_LINKS = [
  { label: 'Create a contract', to: '/signup' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Guides', to: '/guides' },
  { label: 'How scoring works', to: '/score-methodology' },
  { label: 'Contact', to: '/contact' },
  { label: 'Log in', to: '/login' },
  { label: 'Privacy', to: '/privacy' },
  { label: 'Terms', to: '/terms' },
];

export default function FooterRevamp() {
  return (
    <footer className="relative bg-black pt-20 pb-8 px-6 overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_50%_40%_at_50%_0%,rgba(60,180,79,0.05),transparent)]" />
      <div className="absolute inset-0 flex items-center justify-center mt-68">
        <AnimatedOwlIcon className="w-[600px] opacity-5" />
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 md:h-32 bg-gradient-to-t from-black to-transparent" />
      <div className="relative z-10 max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 pb-10">
          <svg
            width="169"
            height="44"
            viewBox="0 0 169 44"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className='-ml-10'
          >
            <path d="M145.939 25.0402L139.229 33.9996H145.255L148.883 29.0512L152.74 33.9996H158.765L152.056 25.0402L158.765 16.1025H152.74L148.883 21.0291L145.255 16.1025H139.229L145.939 25.0402Z" fill="white" />
            <path d="M121.91 33.9031H116.843L116.82 9.44457L121.91 7.94043V33.9031Z" fill="white" />
            <path d="M130.195 33.9246H125.128L125.105 9.46605L130.195 7.96191V33.9246Z" fill="white" />
            <path d="M133.457 33.8391V16.9884L138.478 15.3535V33.8391H133.457Z" fill="white" />
            <path d="M66.1771 9.24807V20.9106L67.5692 22.2839C68.3528 20.075 71.2527 15.6875 76.584 15.8096C83.2481 15.9622 85.188 20.6708 85.8726 22.4583C86.4204 23.8883 86.3291 26.2223 86.215 27.2105H72.8868C73.8301 28.6565 76.5886 30.8335 80.0758 27.9735L83.6133 31.1562C82.495 32.3842 79.1127 34.6222 74.53 33.7502C73.465 33.525 71.3577 32.5949 69.5091 30.8728L60.2661 22.2839C58.9881 20.9251 55.7428 19.0228 52.9858 22.2839C49.5397 26.3604 55.1083 31.6575 59.5587 28.1915L61.2703 26.3604L64.899 29.4122C64.4502 30.2842 62.9774 32.2679 60.6769 33.2271C57.8013 34.426 49.4712 35.3852 47.1662 26.7527C46.7174 24.5874 46.906 19.6593 51.2514 17.2701C55.5967 14.881 59.6499 15.9695 61.1334 16.8124V7.63494L66.1771 9.24807ZM97.8385 10.7522C96.0888 10.7813 92.5802 11.8683 92.5437 15.984H97.8385V20.0386C98.7133 18.6362 101.531 15.8358 105.803 15.8532C111.144 15.875 113.335 19.2103 114.225 20.3874C115.115 21.5646 115.48 23.7227 115.503 25.1396C115.521 26.2731 115.51 26.978 115.503 27.1887H102.083C103.042 28.6347 105.835 30.8117 109.341 27.9517L112.81 31.1343C112.011 32.1153 109.46 34.0598 105.644 33.99C100.874 33.9028 97.9982 30.2406 97.5874 29.3032C97.1766 28.3659 95.7845 25.423 96.8115 22.3275C96.9864 21.8043 97.3957 20.7057 97.6331 20.4964H92.5437V33.881L87.4087 33.9246V15.984C87.3859 12.8086 89.4399 6.36623 97.8385 6V10.7522ZM76.6981 20.4746C74.5375 20.4746 73.1911 21.9279 72.864 22.5455H80.6007C80.1443 21.7971 78.9119 20.4746 76.6981 20.4746ZM105.933 20.4746C103.773 20.4746 102.426 21.9279 102.099 22.5455H109.836C109.38 21.7971 108.147 20.4746 105.933 20.4746Z" fill="white" />
          </svg>

          <ul className="flex flex-wrap items-center justify-center gap-x-7 gap-y-2">
            {NAV_LINKS.map((link) => (
              <li key={link.to}>
                <Link to={link.to} className="text-[14px] text-white/60 hover:text-white transition-colors">
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="pt-6 border-t border-white/[0.06] flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-[12.5px] text-white/35">© {new Date().getFullYear()} Defellix. All rights reserved.</span>
          <span className="text-[12.5px] text-white/30 text-center md:text-right">
            Defellix is a technology provider, not a law firm, and does not provide legal advice.
          </span>
        </div>
      </div >
    </footer >
  );
}
